import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import * as Xstate from "@xstate/react";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import * as devConfig from "../../app/configs/devConfig";
import { ConnectionBanner } from "../../app/connection/ConnectionBanner";
import { ROUTE_NAMES } from "../../app/constants/TextConstants";
import { getAuthHeader } from "../../app/globalState/GlobalAuthState";
import { reactPlugin } from "../../app/insights/AppInsightsPlugin";
import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import Button from "../../components/button/Button";
import { ErrorFallback } from "../../components/errors/ErrorFallback";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import {
  getLinkedFirms,
  getUser,
  setIsLinkedFirm,
} from "../../services/AppService";
import {
  getLinkedFirm,
  setLinkedFirm,
} from "../../sessionStorage/sessionStorage";
import { HelpChat } from "../ai/help/HelpChat";
import { WelcomeModal } from "../onboarding/journey/WelcomeModal";
import { SubscriptionBanner } from "../subscription/SubscriptionBanner";
import { SubscriptionLockPage } from "../subscription/SubscriptionLockPage";
import {
  useCheckoutReturn,
  useSubscriptionStatus,
} from "../subscription/useSubscription";
import { MainSidebarNav } from "./MainSidebarNav";
import { MainTopNav } from "./MainTopNav";

const MainViewPages = ({ locationId }: { locationId?: string }) => {
  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const dispatch = useAppDispatch();
  const { user, linkedFirms, isLinkedFirm } = useAppSelector(
    (state) => state.appReducer,
  );

  // Phones (below md): the left menu is hidden and opens as an overlay from
  // the top bar's menu button; it closes again on every page change.
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Seat billing (2026-10-24): trial banners, and the "Add a payment method"
  // page instead of the app when the firm is locked. Existing firms: phase
  // "none", nothing shows.
  const navigate = useNavigate();
  const subscription = useSubscriptionStatus(
    isLinkedFirm ? getLinkedFirm()?.orgId || "" : "",
  );
  // Back from Stripe Checkout: finish (refreshes the status through the cache tag).
  useCheckoutReturn(subscription.orgId);
  const signOut = () => {
    try {
      sessionStorage.clear();
    } catch {
      // ignore
    }
    navigate(ROUTE_NAMES.signOut);
  };

  const [selectedFirm, setSelectedFirm] = useState(linkedFirms?.[0]?.id || "");
  const [error, setError] = useState("");

  useEffect(() => {
    if (linkedFirms?.length > 0) {
      if (linkedFirms?.length === 1) {
        handleClickGo(linkedFirms?.[0]?.id);
      } else {
        setSelectedFirm(linkedFirms?.[0]?.id);
      }
    }
  }, [linkedFirms]);

  useLayoutEffect(() => {
    if (userInfoByEmail?.id) {
      dispatch(
        getUser({
          userId: `${userInfoByEmail?.id}`,
          orgId: `${userInfoByEmail?.org?.id}`,
        }),
      );
    }
  }, [userInfoByEmail?.id]);

  useEffect(() => {
    if (user) {
      dispatch(setIsLinkedFirm(Boolean(getLinkedFirm())));
    }
  }, [user]);

  useEffect(() => {
    if (user?.id && !isLinkedFirm) {
      dispatch(getLinkedFirms(`${user?.id}`));
    }
  }, [user, isLinkedFirm]);

  const handleClickGo = async (firm: string) => {
    if (firm) {
      const response = await fetch(
        `${devConfig.getServerBaseUrlForEnv()}/api/user-organization/add-link`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: getAuthHeader().Authorization,
          },
          method: "POST",
          body: JSON.stringify({
            userId: `${user?.id}`,
            orgId: `${firm}`,
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        dispatch(
          getUser({
            userId: `${user?.id}`,
            orgId: user?.orgId,
          }),
        );
        setLinkedFirm(data);
        dispatch(setIsLinkedFirm(true));
        setError("");
      } else {
        setError("Something went wrong");
        dispatch(setIsLinkedFirm(false));
      }
    }
  };

  const renderLinkedFirms = () => {
    if (linkedFirms?.length < 2) {
      return <></>;
    }

    return (
      <div>
        <div className={"flex h-screen w-screen items-center justify-center"}>
          <div
            className={
              "w-[480px] rounded-[35px] px-[60px] py-[30px] shadow-card"
            }>
            <div
              className={
                "text-purple w-full text-center text-[25px] font-bold"
              }>
              Procharted
            </div>
            <div className={"my-6 w-full text-center text-[20px] font-bold"}>
              Choose a firm
            </div>
            <div className={"mb-4"}>
              <select
                value={selectedFirm}
                onChange={(e) => setSelectedFirm(e.target.value)}
                className={"select select-bordered w-full"}>
                {linkedFirms?.map((firm) => (
                  <option value={firm?.id} key={firm?.id}>
                    {firm?.legalName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Button
                label={"Go"}
                disabled={!selectedFirm}
                size={"small"}
                colorType={"secondary"}
                onClick={async () => {
                  await handleClickGo(selectedFirm);
                }}
                extraClasses={"normal-case px-6"}
              />
              {error && (
                <div className={"mt-2 text-sm text-red-500"}>{error}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderApp = () => {
    if (subscription.status?.phase === "locked") {
      return (
        <SubscriptionLockPage
          status={subscription.status}
          orgId={subscription.orgId}
          onSignOut={signOut}
        />
      );
    }
    return (
      <div className={"flex flex-col justify-start"}>
        <MainTopNav
          isMobileMenuOpen={isMobileMenuOpen}
          onToggleMobileMenu={() => setIsMobileMenuOpen((open) => !open)}
        />
        <div
          className={
            "flex h-screen flex-grow justify-center bg-[#FAFCFF] pt-16 text-start"
          }>
          {/* side-nav sets the location */}
          <MainSidebarNav
            isMobileOpen={isMobileMenuOpen}
            onMobileClose={() => setIsMobileMenuOpen(false)}
          />

          {/* render content only if location id is not empty */}
          {!!locationId ? (
            <div
              className={
                "h-full w-full min-w-[75vw] overflow-y-auto px-8 max-md:min-w-0 max-md:px-4"
              }
              id={"proc-main-view"}>
              <div className={"w-full pb-20 pt-4"}>
                <AppInsightsErrorBoundary
                  onError={ErrorFallback}
                  appInsights={reactPlugin}>
                  <>
                    <SubscriptionBanner
                      status={subscription.status}
                      orgId={subscription.orgId}
                    />
                    {/* render main route */}
                    <Outlet />
                  </>
                </AppInsightsErrorBoundary>
              </div>
            </div>
          ) : (
            <div
              className={
                "h-full w-full min-w-[75vw] overflow-y-auto px-8 max-md:min-w-0 max-md:px-4"
              }></div>
          )}
        </div>
        {/* "Help" button + panel (bottom right) on every signed-in page. */}
        {!!locationId && <HelpChat />}
        {/* Confetti + welcome after joining a firm / setting one up (2026-10-25). */}
        {!!locationId && <WelcomeModal />}
        <ConnectionBanner />
      </div>
    );
  };

  const renderContent = () => {
    if (isLinkedFirm) {
      return renderApp();
    } else {
      return renderLinkedFirms();
    }
  };

  return renderContent();
};

export default MainViewPages;
